import React, { useState } from 'react';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaCheck, FaTimes } from 'react-icons/fa';
import { addDocument } from '../firebase/firestore';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    role: '',
    email: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Check if Firebase is configured
      const { db } = await import('../firebase/config');

      if (!db) {
        // Demo mode
        console.log('Demo mode: Form data would be submitted:', formData);
        setSuccess(true);
        setFormData({ name: '', organization: '', role: '', email: '', description: '' });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        // Firebase is configured
        await addDocument('messages', {
          ...formData,
          read: false,
          timestamp: new Date()
        });

        // Send Email via FormSubmit.co
        try {
          await fetch('https://formsubmit.co/ajax/digisahyadriit@gmail.com', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              ...formData,
              _subject: `New Conversation Started by ${formData.name}`,
              _template: 'table'
            }),
          });
        } catch (mailError) {
          console.error('FormSubmit failed:', mailError);
        }

        setSuccess(true);
        setFormData({ name: '', organization: '', role: '', email: '', description: '' });
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page-wrapper">
      <div className="page-header">
        <div className="container">
          {/* <span className="contact-label">CONTACT US</span> */}
          <h1 className="contact-hero-title">Start a Conversation</h1>
          <p className="contact-hero-subtitle">Every meaningful transformation begins with a conversation</p>
        </div>
      </div>

      <div className="contact-content-container container">
        {/* Intro Section - Redesigned Statement Box */}
        <div className="contact-intro-statement" data-aos="fade-up">
          <div className="intro-quote-box">
            <div className="quote-accent"></div>
            <p className="primary-statement">
              At Triology, we believe the first step is not a proposal or a presentation it is a <strong>thoughtful conversation</strong>.
            </p>
            <p className="secondary-statement">
              This helps both sides understand context intent and readiness before any engagement begins.
            </p>
          </div>
          <div className="cta-invitation">
            <p>
              If your organization is navigating growth, complexity, or transition,
              we invite you to reach out and <strong>start that conversation</strong>.
            </p>
          </div>
        </div>

        {/* Dual Section Grid: What it is/isn't & When to reach out */}
        <div className="contact-sections-dual-grid" data-aos="fade-up">
          {/* Diagnostic Section */}
          <div className="diagnostic-card">
            <h3 className="section-title-sm">WHAT THIS CONVERSATION IS (AND ISN’T)</h3>
            <p className="diagnostic-subtitle">A diagnostic discussion, not a sales pitch</p>

            <div className="diagnostic-lists">
              <div className="list-group">
                <h4>This initial conversation is designed to:</h4>
                <ul className="check-list">
                  <li><FaCheck className="icon-check" /> understand your organization’s context and challenges</li>
                  <li><FaCheck className="icon-check" /> explore whether Triology is the right partner for you</li>
                  <li><FaCheck className="icon-check" /> share initial perspectives grounded in experience</li>
                </ul>
              </div>

              <div className="list-group">
                <h4>It is not:</h4>
                <ul className="times-list">
                  <li><FaTimes className="icon-times" /> a product pitch</li>
                  <li><FaTimes className="icon-times" /> a commitment to engage</li>
                  <li><FaTimes className="icon-times" /> a generic consulting overview</li>
                </ul>
              </div>
            </div>
            <p className="clarity-note">Clarity at the start creates better outcomes later.</p>
          </div>

          {/* When to Reach Out */}
          <div className="when-to-reach-section">
            <div className="reaching-header">
              <h3 className="section-title-sm">WHEN TO REACH OUT</h3>
              <p className="reaching-subtitle">You may find value in connecting with us if</p>
            </div>

            <div className="reaching-grid">
              <div className="reaching-item">
                <span className="dot"></span>
                <p>growth has introduced complexity or execution strain</p>
              </div>
              <div className="reaching-item">
                <span className="dot"></span>
                <p>leadership alignment feels stretched</p>
              </div>
              <div className="reaching-item">
                <span className="dot"></span>
                <p>strategy is not translating into consistent outcomes</p>
              </div>
              <div className="reaching-item">
                <span className="dot"></span>
                <p>you are preparing for the next phase of scale</p>
              </div>
            </div>
            <p className="unsure-note">Even if you are unsure, a conversation can bring clarity.</p>
          </div>
        </div>

        {/* How to Connect Header */}
        <div className="section-header-centered" data-aos="fade-up">
          <h3 className="section-title-sm">HOW TO CONNECT</h3>
          <h2 className="connect-main-title">Start the conversation</h2>
          <p className="connect-desc">
            Use the form below to share a brief overview of your context.
            A member of the Triology team will respond to schedule a conversation.
          </p>
        </div>

        {/* Form Layout */}
        <div className="connect-form-layout" data-aos="fade-up">
          <div className="connect-info">

            <div className="alternative-contact">
              <h4>CONTACT US</h4>
              <p>If you prefer, you may also reach us directly</p>
              <div className="alt-items">
                <div className="alt-item">
                  <FaEnvelope className="alt-icon" />
                  <a href="mailto:thetriology.solutions@gmail.com">thetriology.solutions@gmail.com</a>
                </div>
                <div className="alt-item">
                  <FaPhone className="alt-icon" />
                  <a href="tel:+919175301171">+91 91753 01171</a>
                </div>
                <div className="alt-item">
                  <FaMapMarkerAlt className="alt-icon" />
                  <span>Gajanan World Tourism, 2nd Floor, Siddhivinayak Apartment, Land Mark Pushparaj Chowk, Sangli</span>
                </div>
              </div>
            </div>

            <div className="contact-map-wrapper">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d597.6731320186194!2d74.5795703543695!3d16.853834823903597!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc1186fc04e3fb9%3A0xd11f81b47f80256d!2sPushparaj%20Chowk%2C%204%2F28%2C%20South%20Shivaji%20Nagar%2C%20Nishant%20Colony%2C%20Sangli%2C%20Sangli%20Miraj%20Kupwad%2C%20Maharashtra%20416416!5e0!3m2!1sen!2sin!4v1765348855573!5m2!1sen!2sin"
                width="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Triology Sangli Office"
              ></iframe>
            </div>
          </div>

          <div className="form-card">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Organization</label>
                  <input
                    type="text"
                    name="organization"
                    placeholder="Your Organization"
                    required
                    value={formData.organization}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <input
                    type="text"
                    name="role"
                    placeholder="Your Role"
                    required
                    value={formData.role}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@email.com"
                  required
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Brief description of your context or challenge</label>
                <textarea
                  name="description"
                  placeholder="Share a brief overview..."
                  required
                  value={formData.description}
                  onChange={handleChange}
                ></textarea>
              </div>

              <button type="submit" className="contact-submit-btn" disabled={loading}>
                {loading ? 'SENDING...' : 'Start the conversation'}
              </button>

              {success && (
                <div className="success-message">
                  <FaCheck /> Message sent successfully! We will get back to you soon.
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Closing Section */}
        <div className="contact-closing-section" data-aos="fade-up">
          <h2 className="closing-title">Clarity is a powerful starting point</h2>
          <p className="closing-text">
            Whether or not we work together, we believe clarity is always valuable.
          </p>
          <p className="closing-text bold">
            If you’re ready to explore what alignment and disciplined execution could look like for your organization, we look forward to hearing from you.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
